import type { Id, Money, OrderLineItem } from "@estelle/domain";

import { sumMoney } from "@/modules/gst/format";

/**
 * What a line item's `variantId` resolves to, for display.
 *
 * Built from `CatalogRepository.getProduct` per variant until #161 removed
 * that caller (a ~100-product N+1 that measured over 12s against
 * nonprod-shaped data — past the ticket's 2s bar, with no bulk "every
 * variant" read to batch it through instead). The shape stays: an empty
 * index is a legitimate value now, not just an edge case, and this is what a
 * future bulk `CatalogRepository` read should still produce.
 */
export interface VariantLabel {
  productTitle: string;
  /** Blank for a single-variant product — see `OrderLineItem.title`. */
  variantTitle: string;
  sku: string | null;
}

/** One product/variant's totals across every line item that sold it. */
export interface ProductSalesRow {
  key: string;
  productTitle: string;
  variantTitle: string;
  sku: string | null;
  quantity: number;
  revenue: Money;
}

/**
 * Sums quantity and line value per variant across every order line passed in
 * (FR-DSH-3 / ticket #96).
 *
 * `OrderLineItem` carries no product name — only `variantId`, and a variant
 * title that is blank for the common single-variant case — so `variantIndex`
 * is required rather than optional: an empty map is an explicit choice a
 * caller makes (ticket #161 — see `VariantLabel`'s doc comment), where a
 * missing argument would be a bug at the call site a type error should catch
 * instead.
 *
 * A deleted variant (`variantId` null, or one `variantIndex` has no entry
 * for) still sold something — it groups under its own SKU rather than being
 * dropped, because a best-sellers report that quietly excluded discontinued
 * lines would understate the top seller the moment it went out of stock.
 */
export function aggregateProductSales(
  lineItems: readonly OrderLineItem[],
  variantIndex: ReadonlyMap<Id, VariantLabel>,
): ProductSalesRow[] {
  interface Accumulator {
    label: VariantLabel;
    quantity: number;
    revenue: Money[];
  }

  const groups = new Map<string, Accumulator>();

  for (const line of lineItems) {
    const label = line.variantId ? variantIndex.get(line.variantId) : undefined;
    const key = line.variantId ?? line.sku ?? line.id;

    let group = groups.get(key);
    if (!group) {
      const split = splitLineTitle(line.title);
      group = {
        label: label ?? {
          productTitle: split.productTitle || "(unknown product)",
          variantTitle: split.variantTitle,
          sku: line.sku,
        },
        quantity: 0,
        revenue: [],
      };
      groups.set(key, group);
    }

    group.quantity += line.quantity;
    group.revenue.push(line.lineTotal);
  }

  return Array.from(groups.entries()).map(([key, group]) => ({
    key,
    productTitle: group.label.productTitle,
    variantTitle: group.label.variantTitle,
    sku: group.label.sku,
    quantity: group.quantity,
    revenue: sumMoney(group.revenue),
  }));
}

/**
 * A line item's title — `Velin Ring — Gold / 7`, as the API and the
 * fixtures both write it when the variant is a real one — as its product and
 * its variant (#639), so a ranking can show the variant on its own line.
 *
 * Split at the last " — ": a product name may hold a dash, a variant title
 * ("Gold / 7") does not.
 */
export function splitLineTitle(title: string): {
  productTitle: string;
  variantTitle: string;
} {
  const at = title.lastIndexOf(" — ");
  if (at <= 0) return { productTitle: title.trim(), variantTitle: "" };
  return {
    productTitle: title.slice(0, at).trim(),
    variantTitle: title.slice(at + 3).trim(),
  };
}

export function topByQuantity(
  rows: readonly ProductSalesRow[],
  limit: number,
): ProductSalesRow[] {
  return [...rows].sort((a, b) => b.quantity - a.quantity).slice(0, limit);
}

export function topByRevenue(
  rows: readonly ProductSalesRow[],
  limit: number,
): ProductSalesRow[] {
  return [...rows]
    .sort((a, b) => Number(b.revenue) - Number(a.revenue))
    .slice(0, limit);
}
